package com.evoapps.evoshift.data.dto.hiring

import kotlinx.serialization.Serializable

/** job_posts/{id} as read (SH-125). The server adds `counts` and `closedAt`. */
@Serializable
data class JobPostDto(
    val id: String = "",
    val workplaceId: String = "",
    val status: String = "DRAFT",
    val title: String = "",
    val role: String = "",
    val description: String = "",
    val hoursText: String = "",
    val contractType: String = "EMPLOYMENT",
    val payMin: Double = 0.0,
    val payMax: Double = 0.0,
    val payUnit: String = "HOUR",
    val currency: String = "PLN",
    val city: String = "",
    val closesOn: String? = null,
    val employerName: String = "",
    val privacyContact: String = "",
    val lang: String = "pl",
    val createdAt: String = "",
    val createdBy: String? = null,
    val updatedAt: String = "",
    val updatedBy: String? = null,
    val counts: JobPostCountsDto? = null,
    val closedAt: String? = null,
    /** SH-210: the post's CV setting, recruiters and rejection e-mail (missing on older posts). */
    val cv: JobPostCvDto? = null,
    val recruiterIds: List<String> = emptyList(),
    val rejectionMail: RejectionMailDto? = null
)

/** SH-210: `cv` of a post: REQUIRED | OPTIONAL | NONE, file kinds (pdf, doc, docx, jpg, png), MB. */
@Serializable
data class JobPostCvDto(
    val requirement: String = "OPTIONAL",
    val types: List<String> = listOf("pdf", "docx"),
    val maxMb: Int = 5
)

/** SH-210: `rejectionMail` of a post (blank texts: the default). */
@Serializable
data class RejectionMailDto(
    val enabled: Boolean = false,
    val subject: String = "",
    val body: String = ""
)

@Serializable
data class JobPostCountsDto(val total: Int = 0, val new: Int = 0)

/**
 * A job post as the app writes it: exactly the fields firestore.rules (validJobPost) accept from
 * a client, merged so the server's counts and closedAt stay.
 */
@Serializable
data class JobPostWriteDto(
    val id: String,
    val workplaceId: String,
    val status: String,
    val title: String,
    val role: String,
    val description: String,
    val hoursText: String,
    val contractType: String,
    val payMin: Double,
    val payMax: Double,
    val payUnit: String,
    val currency: String,
    val city: String,
    val closesOn: String?,
    val employerName: String,
    val privacyContact: String,
    val lang: String,
    val createdAt: String,
    val createdBy: String?,
    val updatedAt: String,
    val updatedBy: String?,
    val cv: JobPostCvDto,
    val recruiterIds: List<String>,
    val rejectionMail: RejectionMailDto
)

/** job_posts/{id}/applications/{appId} as read (written by functions/hiring.js only). */
@Serializable
data class JobApplicationDto(
    val id: String = "",
    val postId: String = "",
    val workplaceId: String = "",
    val name: String = "",
    val email: String? = null,
    val phone: String? = null,
    val availability: String? = null,
    val note: String? = null,
    val consents: ApplicantConsentsDto? = null,
    val cv: ApplicantCvDto? = null,
    val status: String = "NEW",
    val managerNote: String? = null,
    val decidedAt: String? = null,
    val createdAt: String = "",
    val lang: String = "pl",
    /** SH-210: each reader's own read state and favourites (their uids). */
    val readBy: List<String> = emptyList(),
    val starredBy: List<String> = emptyList(),
    /** SH-210: when the rejection e-mail was queued (hiringPipeline.js). */
    val rejectionMailQueuedAt: String? = null
)

@Serializable
data class ApplicantConsentsDto(
    val noticeAcknowledgedAt: String? = null,
    val futureRecruitment: Boolean = false,
    val futureRecruitmentAt: String? = null
)

@Serializable
data class ApplicantCvDto(
    val fileName: String = "",
    val contentType: String = "",
    val sizeBytes: Long = 0,
    val path: String = ""
)
